import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, SlideOver } from '@teals/ui-web';
import {
  useCreateAgentMutation,
  useGetAgentsQuery,
  useGetDataSourcesQuery,
  useGetMcpIntegrationsQuery,
  useGetOrgModelsQuery,
} from '../../store/api';
import type { DataSource, DsType } from '../../store/api/dataSourcesApi';
import type { McpIntegration } from '../../store/api/mcpIntegrationsApi';
import type { OrgModel } from '../../store/api/orgModelsApi';
import {
  DS_TYPE_DESCRIPTIONS,
  DS_TYPE_LABELS,
} from '../DataSources/constants';
import { useAuth } from '../../hooks/useAuth';
import BuilderChat from './BuilderChat';
import {
  EMPTY_AGENT_CONFIG,
  INTEGRATION_DRAG_TYPE,
  mergeDroppedRefs,
  type BuilderAttachment,
  type ProposedConfig,
} from './builderHelpers';

const DOCS = 'https://www.luminafactory.com/docs';
const PREVIEW_LIMIT = 3;

interface DetailFact {
  label: string;
  value: string;
}

interface IntegrationItem {
  key: string;
  name: string;
  kind: 'data_source' | 'mcp';
  label: string;
  summary: string;
  facts: DetailFact[];
}

const GUIDE = [
  {
    title: 'Describe what the agent should do',
    href: `${DOCS}/create-an-agent.html`,
  },
  {
    title: 'Add integrations',
    href: `${DOCS}/integrations.html`,
  },
  {
    title: 'Add preferred models',
    href: `${DOCS}/models.html`,
  },
  {
    title: 'Output structures',
    href: `${DOCS}/output.html`,
  },
] as const;

const Page = styled.div`
  max-width: 72rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 3rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.2;
`;

const BackButton = styled(Button)`
  && {
    height: 36px;
    padding: 0 0.75rem;
    flex-shrink: 0;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  max-width: 38rem;
  color: var(--text-muted);
  font-size: 0.9375rem;
  line-height: 1.5;
`;

const McpLine = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.875rem;
  color: var(--text-secondary, var(--text));

  a {
    color: var(--accent);
    font-weight: 600;
  }
`;

const Guides = styled.details`
  margin-top: 0.85rem;

  summary {
    width: fit-content;
    cursor: pointer;
    list-style: none;
    font-size: 0.8125rem;
    font-weight: 500;
    color: var(--text-secondary, var(--text));
  }

  summary::-webkit-details-marker {
    display: none;
  }

  summary:hover,
  &[open] summary {
    color: var(--text-primary, var(--text));
  }

  summary:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 3px;
    border-radius: 4px;
  }

  ol {
    margin: 0.6rem 0 0;
    padding-left: 1.15rem;
    max-width: 36rem;
  }

  li {
    margin: 0.3rem 0;
    font-size: 0.8125rem;
    color: var(--text-secondary, var(--text));
  }

  a {
    margin-left: 0.45rem;
    color: var(--accent);
    font-weight: 600;
  }
`;

const Workspace = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  align-items: start;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 1fr) 18rem;
  }
`;

const TrayStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TrayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.65rem;
`;

const TrayActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
`;

const TrayIconButton = styled(Button)`
  && {
    box-sizing: border-box;
    width: 32px;
    height: 32px;
    min-width: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
`;

const TrayPlus = styled(TrayIconButton)`
  && {
    font-size: 1.125rem;
    font-weight: 600;
  }
`;

const TrayTitle = styled.h2`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
`;

const TrayHint = styled.p`
  margin: 0 0 0.65rem;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ChipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const DragChip = styled.button`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
  text-align: left;
  padding: 0.4rem 0.55rem;
  border-radius: 10px;
  border: 1px dashed color-mix(in srgb, var(--accent) 45%, var(--border));
  background: var(--bg-elevated, var(--surface-2, #f8fafc));
  color: var(--text);
  font: inherit;
  font-size: 0.8125rem;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const ModelChip = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  padding: 0.4rem 0.55rem;
  border-radius: 10px;
  border: 1px solid var(--border);
  background: var(--bg-surface, var(--surface));
  color: var(--text);
  font: inherit;
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover {
    border-color: var(--accent);
  }
`;

const Grip = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  opacity: 0.55;

  span {
    display: block;
    width: 10px;
    height: 2px;
    border-radius: 1px;
    background: var(--text-muted);
  }
`;

const Kind = styled.span`
  margin-left: auto;
  font-size: 0.6875rem;
  color: var(--text-muted);
`;

const EmptyCopy = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const PickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
`;

const PickerRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
`;

const PickerMeta = styled.span`
  display: block;
  margin-top: 0.15rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted);
`;

const DetailCard = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const DetailSummary = styled.p`
  margin: 0.4rem 0 0 1.55rem;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--text-secondary, var(--text));
`;

const FactList = styled.dl`
  margin: 0.45rem 0 0 1.55rem;
  display: grid;
  grid-template-columns: 5.5rem minmax(0, 1fr);
  gap: 0.2rem 0.5rem;
  font-size: 0.75rem;
`;

const FactLabel = styled.dt`
  margin: 0;
  color: var(--text-muted);
`;

const FactValue = styled.dd`
  margin: 0;
  color: var(--text);
`;

function dsTypeLabel(type?: string): string {
  if (!type) return 'Data source';
  return DS_TYPE_LABELS[type as DsType] || type;
}

function integrationSummary(ds: DataSource): string {
  const written = ds.description?.trim();
  if (written) return written;
  return (
    DS_TYPE_DESCRIPTIONS[ds.dsType] ||
    'A data source this agent can read. Secrets stay on the connection.'
  );
}

function mcpSummary(row: McpIntegration): string {
  const written = row.description?.trim();
  if (written) return written;
  return 'A Custom MCP server this agent can call. Secrets stay on the connection.';
}

function dataSourceFacts(ds: DataSource): DetailFact[] {
  const facts: DetailFact[] = [
    { label: 'Type', value: dsTypeLabel(ds.dsType) },
    { label: 'Status', value: ds.status || 'unknown' },
  ];
  if (typeof ds.recordCount === 'number') {
    facts.push({ label: 'Records', value: String(ds.recordCount) });
  }
  return facts;
}

function mcpFacts(row: McpIntegration): DetailFact[] {
  const tools = (row.discoveredTools || [])
    .map((tool) => tool.name)
    .filter(Boolean);
  return [
    {
      label: 'Sign-in',
      value:
        row.authMode === 'stored_credentials'
          ? 'Stored credentials'
          : 'Caller token',
    },
    { label: 'Status', value: row.status || 'unknown' },
    {
      label: 'Tools',
      value: tools.length ? tools.slice(0, 6).join(', ') : 'None discovered yet',
    },
  ];
}

function modelFacts(model: OrgModel): DetailFact[] {
  const facts: DetailFact[] = [{ label: 'Id', value: model.productId }];
  if (model.lab) facts.push({ label: 'Lab', value: model.lab });
  if (model.family) facts.push({ label: 'Family', value: model.family });
  if (model.route || model.provider) {
    facts.push({ label: 'Route', value: model.route || model.provider });
  }
  if (model.capabilities?.length) {
    facts.push({
      label: 'Can',
      value: model.capabilities.slice(0, 6).join(', '),
    });
  }
  return facts;
}

function modelSummary(model: OrgModel): string {
  const bits = [model.displayName];
  if (model.lab) bits.push(`from ${model.lab}`);
  return `${bits.join(' ')} is enabled for chat in this organisation.`;
}

function startIntegrationDrag(
  event: React.DragEvent,
  ref: BuilderAttachment,
) {
  const payload = JSON.stringify(ref);
  event.dataTransfer.setData(INTEGRATION_DRAG_TYPE, payload);
  event.dataTransfer.setData('text/plain', ref.name);
  event.dataTransfer.effectAllowed = 'copy';
}

export function startFromModelBrief(model: OrgModel, instructions?: string | null): string {
  const lines = [
    `Create an agent that uses ${model.displayName} (${model.productId}) as its default model.`,
  ];
  const trimmed = instructions?.trim();
  if (trimmed) lines.push(`Instructions: ${trimmed}`);
  return lines.join('\n');
}

const AgentBuilderCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startModelId = searchParams.get('model');
  const startInstructions = searchParams.get('instructions');
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const { data: catalog = [] } = useGetAgentsQuery(
    { orgId: orgId || '' },
    { skip: !orgId },
  );
  const dataSourcesQuery = useGetDataSourcesQuery(
    { orgId: orgId || '' },
    { skip: !orgId },
  );
  const mcpQuery = useGetMcpIntegrationsQuery(
    { orgId: orgId || '' },
    { skip: !orgId },
  );
  const modelsQuery = useGetOrgModelsQuery(orgId || '', {
    skip: !orgId,
  });
  const orgDataSources = dataSourcesQuery.data;
  const mcpIntegrations = mcpQuery.data;
  const orgModels = modelsQuery.data;
  const integrationsPending =
    Boolean(orgId) &&
    ((!dataSourcesQuery.isSuccess && !dataSourcesQuery.isError) ||
      (!mcpQuery.isSuccess && !mcpQuery.isError));
  const modelsPending =
    Boolean(orgId) && !modelsQuery.isSuccess && !modelsQuery.isError;
  const [createAgent] = useCreateAgentMutation();
  const [hasContent, setHasContent] = useState(false);
  const [composerSeed, setComposerSeed] = useState<{
    id: number;
    text: string;
  } | null>(null);
  const [attachSeed, setAttachSeed] = useState<{
    id: number;
    refs: BuilderAttachment[];
  } | null>(null);
  const [picker, setPicker] = useState<'integrations' | 'models' | null>(null);
  const [pickedIntegrationKeys, setPickedIntegrationKeys] = useState<string[]>(
    [],
  );
  const [pickedModelIds, setPickedModelIds] = useState<string[]>([]);

  const chatModels = useMemo(
    () =>
      (orgModels || []).filter(
        (model) => model.enabled && model.chatCapable !== false,
      ),
    [orgModels],
  );

  const integrationItems = useMemo<IntegrationItem[]>(
    () => [
      ...(orgDataSources || []).map((ds) => ({
        key: ds.dataSourceId,
        name: ds.name,
        kind: 'data_source' as const,
        label: 'Data source',
        summary: integrationSummary(ds),
        facts: dataSourceFacts(ds),
      })),
      ...(mcpIntegrations || []).map((row) => ({
        key: row.mcpId,
        name: row.name,
        kind: 'mcp' as const,
        label: 'Custom MCP',
        summary: mcpSummary(row),
        facts: mcpFacts(row),
      })),
    ],
    [orgDataSources, mcpIntegrations],
  );
  const startModel = useMemo(
    () =>
      startModelId
        ? chatModels.find((model) => model.productId === startModelId) || null
        : null,
    [chatModels, startModelId],
  );
  const startModelUnavailable =
    Boolean(startModelId) && !modelsPending && startModel == null;
  const startSeeded = useRef(false);

  useEffect(() => {
    if (startSeeded.current || !startModel) return;
    startSeeded.current = true;
    setComposerSeed({
      id: Date.now(),
      text: startFromModelBrief(startModel, startInstructions),
    });
  }, [startModel, startInstructions]);

  const previewIntegrations = integrationItems.slice(0, PREVIEW_LIMIT);
  const previewModels = useMemo(() => {
    if (!startModel) return chatModels.slice(0, PREVIEW_LIMIT);
    return [
      startModel,
      ...chatModels.filter((model) => model.productId !== startModel.productId),
    ].slice(0, PREVIEW_LIMIT);
  }, [chatModels, startModel]);

  useEffect(() => {
    if (!hasContent) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasContent]);

  const promptContext = useMemo(
    () => ({
      mode: 'create',
      edit_mode: false,
      read_only: false,
      catalog_agents: catalog.slice(0, 40).map((a) => ({
        agent_name: a.agentName,
        name: a.name,
        agent_type: a.agentType,
      })),
      catalog_data_sources: (orgDataSources || []).slice(0, 40).map((d) => ({
        data_source_id: d.dataSourceId,
        name: d.name,
      })),
      catalog_mcp_servers: (mcpIntegrations || []).slice(0, 40).map((row) => ({
        mcp_id: row.mcpId,
        name: row.name,
      })),
    }),
    [catalog, orgDataSources, mcpIntegrations],
  );

  const confirmLeave = useCallback(() => {
    if (!hasContent) return true;
    return window.confirm(
      'Nothing is saved yet. Leave without creating this agent?',
    );
  }, [hasContent]);

  const handleBack = () => {
    if (!confirmLeave()) return;
    navigate('/agents');
  };

  const handleAccept = async ({
    proposed,
    agentConfig,
    conversationId,
    droppedRefs,
  }: {
    proposed: ProposedConfig;
    agentConfig: Record<string, unknown>;
    conversationId?: string;
    droppedRefs: BuilderAttachment[];
  }) => {
    const merged = mergeDroppedRefs(agentConfig, droppedRefs);
    if (startModel && !merged.llm_model) {
      merged.llm_model = startModel.productId;
    }
    const created = await createAgent({
      ...merged,
      conversation_id: conversationId,
      name: proposed.agentName || (merged.agent_name as string),
    }).unwrap();
    navigate(
      `/agents/${encodeURIComponent(created.id)}?tab=configure&created=1`,
    );
  };

  const preferModel = (model: OrgModel) => {
    setComposerSeed({
      id: Date.now(),
      text: `Prefer ${model.displayName} (${model.productId}) as a model.`,
    });
  };

  const openPicker = (next: 'integrations' | 'models') => {
    setPickedIntegrationKeys([]);
    setPickedModelIds([]);
    setPicker(next);
  };

  const togglePicked = (
    current: string[],
    id: string,
    setCurrent: (ids: string[]) => void,
  ) => {
    setCurrent(
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const addPicked = () => {
    if (picker === 'integrations') {
      const refs = integrationItems
        .filter((item) => pickedIntegrationKeys.includes(item.key))
        .map((item) => ({ kind: item.kind, name: item.name }));
      if (refs.length) {
        setAttachSeed({ id: Date.now(), refs });
      }
    }
    if (picker === 'models') {
      const lines = chatModels
        .filter((model) => pickedModelIds.includes(model.productId))
        .map(
          (model) =>
            `Prefer ${model.displayName} (${model.productId}) as a model.`,
        );
      if (lines.length) {
        setComposerSeed({ id: Date.now(), text: lines.join(' ') });
      }
    }
    setPicker(null);
  };

  return (
    <Page>
      <div>
        <TitleRow>
          <Title>Create agent</Title>
          <BackButton variant="ghost" onClick={handleBack}>
            Back
          </BackButton>
        </TitleRow>
        <Subtitle>
          Describe the job. Accept a proposal to save a Draft — nothing is
          stored until then.
        </Subtitle>
        <McpLine>
          Build and run agents from Cursor.{' '}
          <Link to="/mcp">Learn and configure MCP</Link>
        </McpLine>
        <Guides data-testid="create-guide">
          <summary>How this works</summary>
          <ol>
            {GUIDE.map((step) => (
              <li key={step.href}>
                {step.title}
                <a href={step.href} target="_blank" rel="noreferrer">
                  Learn
                </a>
              </li>
            ))}
          </ol>
        </Guides>
      </div>
      <Workspace>
        <BuilderChat
          mode="create"
          greeting=""
          subtitle=""
          placeholder="Or describe the agent you want…"
          seedChips={[]}
          promptContext={promptContext}
          metadataSource="create"
          editsEnabled
          currentConfig={EMPTY_AGENT_CONFIG}
          fleetNames={[]}
          dataSourceNames={[]}
          composerSeed={composerSeed}
          attachSeed={attachSeed}
          onAccept={handleAccept}
          onContentChange={setHasContent}
        />
        <TrayStack>
          <Card padding={16} elevation="sm" data-testid="integration-tray">
            <TrayHeader>
              <TrayTitle>Integrations</TrayTitle>
              <TrayActions>
                {!integrationsPending && (
                  <TrayIconButton
                    variant="ghost"
                    size="sm"
                    aria-label="More integrations"
                    onClick={() => openPicker('integrations')}
                  >
                    ···
                  </TrayIconButton>
                )}
                <TrayPlus
                  variant="outline"
                  size="sm"
                  aria-label="Create integration"
                  onClick={() =>
                    navigate('/integrations?view=catalog&returnTo=/agents/new')
                  }
                >
                  +
                </TrayPlus>
              </TrayActions>
            </TrayHeader>
            <TrayHint>Drag into the chat. Secrets stay on the connection.</TrayHint>
            {integrationsPending ? (
              <EmptyCopy>Loading integrations…</EmptyCopy>
            ) : (orgDataSources || []).length === 0 &&
              (mcpIntegrations || []).length === 0 ? (
              <EmptyCopy>No integrations yet.</EmptyCopy>
            ) : (
              <ChipList>
                {previewIntegrations.map((item) => (
                  <DragChip
                    key={item.key}
                    type="button"
                    draggable
                    title="Drag into Agent Builder"
                    data-testid={`integration-chip-${item.name}`}
                    onDragStart={(event) =>
                      startIntegrationDrag(event, {
                        kind: item.kind,
                        name: item.name,
                      })
                    }
                  >
                    <Grip aria-hidden="true">
                      <span />
                      <span />
                      <span />
                    </Grip>
                    {item.name}
                    <Kind>{item.label}</Kind>
                  </DragChip>
                ))}
              </ChipList>
            )}
          </Card>
          <Card padding={16} elevation="sm" data-testid="model-tray">
            <TrayHeader>
              <TrayTitle>Models</TrayTitle>
              <TrayActions>
                {!modelsPending && (
                  <TrayIconButton
                    variant="ghost"
                    size="sm"
                    aria-label="More models"
                    onClick={() => openPicker('models')}
                  >
                    ···
                  </TrayIconButton>
                )}
                <TrayPlus
                  variant="outline"
                  size="sm"
                  aria-label="Add model"
                  onClick={() => navigate('/models')}
                >
                  +
                </TrayPlus>
              </TrayActions>
            </TrayHeader>
            <TrayHint>Enabled for this organisation. Click to prefer one.</TrayHint>
            {startModelUnavailable && (
              <EmptyCopy role="status">
                That model isn&apos;t on for this organisation, so it wasn&apos;t added.
              </EmptyCopy>
            )}
            {modelsPending ? (
              <EmptyCopy>Loading models…</EmptyCopy>
            ) : chatModels.length === 0 ? (
              <EmptyCopy>No models enabled yet.</EmptyCopy>
            ) : (
              <ChipList>
                {previewModels.map((model) => (
                  <ModelChip
                    key={model.productId}
                    type="button"
                    data-testid={`model-chip-${model.productId}`}
                    onClick={() => preferModel(model)}
                  >
                    {model.displayName}
                  </ModelChip>
                ))}
              </ChipList>
            )}
          </Card>
        </TrayStack>
      </Workspace>
      <SlideOver
        isOpen={picker != null}
        onClose={() => setPicker(null)}
        title={picker === 'models' ? 'Models' : 'Integrations'}
        position="right"
        width="md"
      >
        <TrayHint>
          {picker === 'models'
            ? 'Enabled chat models for this organisation. Select any to prefer them in the draft.'
            : 'What each connection can do. Select any to attach them to this draft. Secrets stay on the connection.'}
        </TrayHint>
        <PickerList>
          {picker === 'integrations' && integrationItems.length === 0 && (
            <EmptyCopy>No integrations yet. Use + to create one.</EmptyCopy>
          )}
          {picker === 'integrations' &&
            integrationItems.map((item) => (
              <DetailCard key={item.key}>
                <PickerRow>
                  <input
                    type="checkbox"
                    aria-label={item.name}
                    checked={pickedIntegrationKeys.includes(item.key)}
                    onChange={() =>
                      togglePicked(
                        pickedIntegrationKeys,
                        item.key,
                        setPickedIntegrationKeys,
                      )
                    }
                  />
                  <span>
                    {item.name}
                    <PickerMeta>{item.label}</PickerMeta>
                  </span>
                </PickerRow>
                <DetailSummary>{item.summary}</DetailSummary>
                <FactList>
                  {item.facts.map((fact) => (
                    <React.Fragment key={fact.label}>
                      <FactLabel>{fact.label}</FactLabel>
                      <FactValue>{fact.value}</FactValue>
                    </React.Fragment>
                  ))}
                </FactList>
              </DetailCard>
            ))}
          {picker === 'models' && chatModels.length === 0 && (
            <EmptyCopy>No models enabled yet. Use + to add one.</EmptyCopy>
          )}
          {picker === 'models' &&
            chatModels.map((model) => (
              <DetailCard key={model.productId}>
                <PickerRow>
                  <input
                    type="checkbox"
                    aria-label={model.displayName}
                    checked={pickedModelIds.includes(model.productId)}
                    onChange={() =>
                      togglePicked(
                        pickedModelIds,
                        model.productId,
                        setPickedModelIds,
                      )
                    }
                  />
                  <span>{model.displayName}</span>
                </PickerRow>
                <DetailSummary>{modelSummary(model)}</DetailSummary>
                <FactList>
                  {modelFacts(model).map((fact) => (
                    <React.Fragment key={fact.label}>
                      <FactLabel>{fact.label}</FactLabel>
                      <FactValue>{fact.value}</FactValue>
                    </React.Fragment>
                  ))}
                </FactList>
              </DetailCard>
            ))}
        </PickerList>
        <Button
          variant="primary"
          size="sm"
          disabled={
            picker === 'integrations'
              ? pickedIntegrationKeys.length === 0
              : pickedModelIds.length === 0
          }
          onClick={addPicked}
        >
          Add
        </Button>
      </SlideOver>
    </Page>
  );
};

export default AgentBuilderCreate;
